import { useEffect, useState } from 'react';
import { PageHeader } from '../../../components/layout/Header';
import { Button } from '../../../components/ui';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import Spinner from '../../../components/ui/Spinner';
import Pagination from '../../../components/shared/Pagination';
import { useAdminIntegrations } from '../hooks/useAdminIntegrations';
import { useAdminIntegrationStats } from '../hooks/useAdminIntegrationStats';
import { IntegrationStatsCards } from './IntegrationStatsCards';
import { IntegrationsFilters } from './IntegrationsFilters';
import { IntegrationsTable } from './IntegrationsTable';

const DEBOUNCE_MS = 300;

export function AdminIntegrationsPage() {
    const {
        data: stats,
        isLoading: statsLoading,
        error: statsError,
        refetch: refetchStats,
    } = useAdminIntegrationStats();
    const {
        data,
        isLoading: integrationsLoading,
        error: integrationsError,
        refetch: refetchIntegrations,
        page,
        setPage,
        setQ,
        filters,
        patchFilters,
        pageSize,
    } = useAdminIntegrations();
    const [searchInput, setSearchInput] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setQ(searchInput);
            setPage(1);
        }, DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchInput, setQ, setPage]);

    const isLoading = statsLoading || integrationsLoading;
    const error = statsError ?? integrationsError;
    const total = data?.total ?? 0;

    const handleRetry = () => {
        void refetchStats();
        void refetchIntegrations();
    };

    return (
        <div className="flex flex-col bg-background">
            <PageHeader
                title="Integrações"
                subtitle="Meta Ads e outras integrações (exceto WhatsApp) em todas as empresas"
                titleClassName="text-xl font-semibold tracking-tight text-default"
                subtitleClassName="text-[13px] text-muted"
                className="mb-[18px]"
                hideBackButton
            />

            <IntegrationsFilters
                filters={filters}
                search={searchInput}
                onSearch={setSearchInput}
                onChange={patchFilters}
            />

            {isLoading && (
                <div
                    className="flex items-center justify-center py-s16"
                    role="status"
                    aria-live="polite"
                >
                    <Spinner color="primary" size="lg" />
                </div>
            )}

            {!isLoading && error && (
                <Alert variant="destructive">
                    <AlertTitle>Erro ao carregar</AlertTitle>
                    <AlertDescription className="flex flex-col gap-s3">
                        <p>{error}</p>
                        <Button variant="outline" size="sm" onClick={handleRetry}>
                            Tentar novamente
                        </Button>
                    </AlertDescription>
                </Alert>
            )}

            {!isLoading && !error && stats && data && (
                <>
                    <IntegrationStatsCards stats={stats} />
                    <IntegrationsTable items={data.items} />
                    {total > 0 && (
                        <Pagination
                            className="mt-s4 shrink-0"
                            currentPage={page}
                            totalCount={total}
                            pageSize={pageSize}
                            onPageChange={setPage}
                        />
                    )}
                </>
            )}
        </div>
    );
}
